import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Session } from "@supabase/supabase-js";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

type SubscriptionStatus = 'trial' | 'active' | 'expired' | 'cancelled' | null;

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const [session, setSession] = useState<Session | null>(null);
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        if (session) {
          checkSubscriptionAccess(session.user.id);
        } else {
          setHasAccess(false);
          setSubscriptionStatus(null);
          setLoading(false);
        }
      }
    );

    // Then check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) {
        checkSubscriptionAccess(session.user.id);
      } else {
        setHasAccess(false);
        setSubscriptionStatus(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkSubscriptionAccess = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('status, trial_end_date, subscription_end_date')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) throw error;

      if (!data) {
        // No subscription record - should not happen with trigger, but handle gracefully
        setHasAccess(false);
        setSubscriptionStatus(null);
        setLoading(false);
        return;
      }

      const now = new Date();
      const trialEnd = new Date(data.trial_end_date);
      const isInTrial = data.status === 'trial' && trialEnd > now;
      const isActive = data.status === 'active';
      
      // Auto-update expired trials
      if (data.status === 'trial' && trialEnd <= now) {
        await supabase
          .from('user_subscriptions')
          .update({ status: 'expired', updated_at: new Date().toISOString() })
          .eq('user_id', userId);
        setSubscriptionStatus('expired');
      } else {
        setSubscriptionStatus(data.status as SubscriptionStatus);
      }
      
      setHasAccess(isInTrial || isActive);
    } catch (error) {
      console.error('Error checking subscription:', error);
      setHasAccess(false);
      setSubscriptionStatus(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Not logged in -> go to auth
  if (!session) {
    return <Navigate to="/auth" replace />;
  }

  // Trial expired or cancelled -> go to paywall
  if (hasAccess === false && (subscriptionStatus === 'expired' || subscriptionStatus === 'cancelled' || subscriptionStatus === 'trial')) {
    return <Navigate to="/paywall" replace />;
  }

  // No subscription record at all -> go to auth (edge case)
  if (hasAccess === false && subscriptionStatus === null) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
